"use client";

import { Button, buttonVariants } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LoaderCircle, Trash } from "lucide-react";
import { useState } from "react";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { useStorage } from "@/features/storage/useStorage";
import { useQueryClient } from "@tanstack/react-query";
import { GetLomdaV3ResponseType } from "../../hooks/useGetLomdaV3";
import { t } from "@/lib/localization";

interface DeleteLomdaV3ButtonProps {
    lomda: GetLomdaV3ResponseType["data"];
}

export default function DeleteLomdaV3Button({
    lomda,
}: DeleteLomdaV3ButtonProps) {
    const setOpen = useStorage((state) => state.setLomdaV3SheetOpen);
    const [isPending, setIsPending] = useState(false);
    const queryClient = useQueryClient();
    const deleteDescription = t(
        "storage.delete-lomda-modal.header.description",
        undefined,
        undefined
    );
    const [deleteDescriptionBefore, deleteDescriptionAfter] =
        deleteDescription.split("{lomdaTitle}");

    const closeModal = () => {
        setOpen(false);
        setIsPending(false);
    };

    const submitHandler = async () => {
        setIsPending(true);

        const res = await client.api.storage["lomda-v3"][":lomdaId"].$delete({
            param: {
                lomdaId: lomda.id,
            },
        });

        if (!res.ok) {
            let errorMessage = t("toast.error.delete-lomda");
            try {
                const errorData = await res.json();
                errorMessage =
                    (errorData as { error?: string })?.error || errorMessage;
            } catch {
                const errorText = await res.text();
                console.error("Delete lomdaV3 error:", errorText);
            }
            toast.error(errorMessage, {
                keyName: "toast.error.delete-lomda",
            });
            setIsPending(false);
            return;
        }

        try {
            await res.json();

            queryClient.invalidateQueries({
                queryKey: ["get-storage-files"],
            });

            toast.success(t("toast.success.delete-lomda"), {
                keyName: "toast.success.delete-lomda",
            });
            closeModal();
        } catch (error) {
            console.error("Error parsing delete response:", error);
            toast.error(t("toast.error.delete-lomda"), {
                keyName: "toast.error.delete-lomda",
            });
            setIsPending(false);
        }
    };

    return (
        <AlertDialog>
            <AlertDialogTrigger>
                <div
                    className={buttonVariants({
                        variant: "secondary",
                        size: "sm",
                    })}
                >
                    <Trash className="size-3.5 me-2" />
                    {t("lomda-sheet.header.delete-btn", undefined, undefined)}
                </div>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:max-w-[380px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {t(
                            "storage.delete-lomda-modal.header.title",
                            undefined,
                            undefined
                        )}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {deleteDescriptionBefore}
                        <span className="font-semibold">
                            {lomda?.name || ""}
                        </span>
                        {deleteDescriptionAfter || ""}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex flex-row gap-3">
                    <AlertDialogCancel
                        disabled={isPending}
                        className="flex justify-center items-center"
                    >
                        {t(
                            "storage.delete-lomda-modal.cancel-btn",
                            undefined,
                            undefined
                        )}
                    </AlertDialogCancel>
                    <Button
                        disabled={isPending}
                        onClick={() => submitHandler()}
                        variant="destructive"
                        className="flex justify-center items-center"
                    >
                        {isPending ? (
                            <LoaderCircle className="size-4 text-white animate-spin" />
                        ) : (
                            t(
                                "storage.delete-lomda-modal.delete-btn",
                                undefined,
                                undefined
                            )
                        )}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
